import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import type { UploadFile } from 'antd/es/upload/interface';
import PenIcon from 'assets/icons/pen.svg';
import MailingService, {
  CreateMailingContactRequest,
  MailingContact,
  MailingContactImportResult,
  UpdateMailingContactRequest,
} from '../../services/MailingService';
import { getMailingSourceLabel } from './mailingLabels';

const DEFAULT_PAGE_SIZE = 10;
type ContactStatusFilter = 'ACTIVE' | 'PASSIVE';
type ContactCreateTab = 'manual' | 'import';
type ContactSourceFilter = 'ALL' | 'MANUAL' | 'IMPORT';

export interface MailingContactsTabProps {
  contacts: MailingContact[];
  loading: boolean;
  onReload: () => Promise<void>;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
};

const getErrorMessage = (error: any, fallback: string) => error?.response?.data?.message || fallback;

const normalizeSearchText = (value?: string | null) => (value || '').trim().toLocaleLowerCase('tr-TR');

const getContactSource = (contact: MailingContact) => (contact.source === 'IMPORT' ? 'IMPORT' : 'MANUAL');

const formatContactSourceTag = (contact: MailingContact) => {
  const source = getContactSource(contact);
  if (source === 'IMPORT') return <Tag color='processing'>{getMailingSourceLabel(source)}</Tag>;
  return <Tag>{getMailingSourceLabel(source)}</Tag>;
};

const MailingContactsTab: React.FC<MailingContactsTabProps> = ({ contacts, loading, onReload }) => {
  const { Text } = Typography;

  const [selectedStatusFilter, setSelectedStatusFilter] = useState<ContactStatusFilter>('ACTIVE');
  const [pendingStatusFilter, setPendingStatusFilter] = useState<ContactStatusFilter>('ACTIVE');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [selectedSourceFilter, setSelectedSourceFilter] = useState<ContactSourceFilter>('ALL');
  const [pendingSourceFilter, setPendingSourceFilter] = useState<ContactSourceFilter>('ALL');
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [pendingTagFilters, setPendingTagFilters] = useState<string[]>([]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [createTab, setCreateTab] = useState<ContactCreateTab>('manual');

  const [editingContact, setEditingContact] = useState<MailingContact | null>(null);
  const [updatingContactStatusId, setUpdatingContactStatusId] = useState<string | null>(null);
  const [creatingContact, setCreatingContact] = useState(false);
  const [updatingContact, setUpdatingContact] = useState(false);
  const [importingContacts, setImportingContacts] = useState(false);

  const [importFileList, setImportFileList] = useState<UploadFile[]>([]);
  const [importResult, setImportResult] = useState<MailingContactImportResult | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [contactForm] = Form.useForm<CreateMailingContactRequest>();
  const [contactEditForm] = Form.useForm<UpdateMailingContactRequest>();

  const debouncedApplySearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedApplySearch.cancel();
    };
  }, [debouncedApplySearch]);

  const tagOptions = useMemo(() => {
    const tags = new Set<string>();
    contacts.forEach((contact) => {
      (contact.tags || []).forEach((tag) => {
        const trimmed = String(tag || '').trim();
        if (trimmed) {
          tags.add(trimmed);
        }
      });
    });
    return Array.from(tags).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const normalizedSearch = normalizeSearchText(search);

    return contacts
      .filter((contact) => {
        if (selectedStatusFilter === 'ACTIVE') {
          return contact.active !== false;
        }
        return contact.active === false;
      })
      .filter((contact) => {
        if (selectedSourceFilter === 'ALL') return true;
        return getContactSource(contact) === selectedSourceFilter;
      })
      .filter((contact) => {
        if (selectedTagFilters.length === 0) return true;
        const tags = Array.isArray(contact.tags) ? contact.tags : [];
        return tags.some((tag) => selectedTagFilters.includes(String(tag)));
      })
      .filter((contact) => {
        if (!normalizedSearch) return true;
        const searchable = [contact.firstName, contact.lastName, contact.email, contact.phone, contact.companyName]
          .map((value) => normalizeSearchText(value))
          .join(' ');
        return searchable.includes(normalizedSearch);
      });
  }, [contacts, search, selectedSourceFilter, selectedStatusFilter, selectedTagFilters]);

  useEffect(() => {
    setPage(1);
  }, [selectedSourceFilter, selectedStatusFilter, selectedTagFilters, search]);

  const openFilterDrawer = () => {
    setPendingStatusFilter(selectedStatusFilter);
    setPendingSourceFilter(selectedSourceFilter);
    setPendingTagFilters(selectedTagFilters);
    setFilterOpen(true);
  };

  const closeFilterDrawer = () => {
    setFilterOpen(false);
  };

  const clearPendingFilters = () => {
    setPendingStatusFilter('ACTIVE');
    setPendingSourceFilter('ALL');
    setPendingTagFilters([]);
  };

  const applyPendingFilters = () => {
    setSelectedStatusFilter(pendingStatusFilter);
    setSelectedSourceFilter(pendingSourceFilter);
    setSelectedTagFilters(pendingTagFilters);
    setPage(1);
    setFilterOpen(false);
  };

  const openCreateDrawer = () => {
    setCreateTab('manual');
    setImportResult(null);
    setImportFileList([]);
    contactForm.resetFields();
    setCreateDrawerOpen(true);
  };

  const closeCreateDrawer = () => {
    setCreateDrawerOpen(false);
    setCreateTab('manual');
    setImportResult(null);
    setImportFileList([]);
    contactForm.resetFields();
  };

  const openEditDrawer = (contact: MailingContact) => {
    setEditingContact(contact);
    contactEditForm.setFieldsValue({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email,
      phone: contact.phone || '',
      companyName: contact.companyName || '',
      tags: Array.isArray(contact.tags) ? contact.tags : [],
    });
    setEditDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    setEditDrawerOpen(false);
    setEditingContact(null);
    contactEditForm.resetFields();
  };

  const handleCreateContact = async () => {
    try {
      setCreatingContact(true);
      const values = await contactForm.validateFields();
      await MailingService.createContact(values);
      message.success('Kişi eklendi');
      closeCreateDrawer();
      await onReload();
    } catch (error) {
      if (!(error as any)?.errorFields) {
        console.error('Contact create failed', error);
        message.error(getErrorMessage(error, 'Kişi eklenemedi'));
      }
    } finally {
      setCreatingContact(false);
    }
  };

  const handleImportContacts = async () => {
    const selectedFile = importFileList[0]?.originFileObj as File | undefined;
    if (!selectedFile) {
      message.error('Lütfen bir .xlsx dosyası seçiniz');
      return;
    }

    try {
      setImportingContacts(true);
      const response = await MailingService.importContacts(selectedFile);
      setImportResult(response.data);
      message.success('Excel import tamamlandı');
      await onReload();
    } catch (error) {
      console.error('Contact import failed', error);
      message.error(getErrorMessage(error, 'Excel import başarısız'));
    } finally {
      setImportingContacts(false);
    }
  };

  const handleCreateDrawerSubmit = async () => {
    if (createTab === 'manual') {
      await handleCreateContact();
      return;
    }
    await handleImportContacts();
  };

  const handleUpdateContact = async () => {
    if (!editingContact) return;

    try {
      setUpdatingContact(true);
      const values = await contactEditForm.validateFields();
      await MailingService.updateContact(editingContact.id, values);
      message.success('Kişi güncellendi');
      closeEditDrawer();
      await onReload();
    } catch (error) {
      if (!(error as any)?.errorFields) {
        console.error('Contact update failed', error);
        message.error(getErrorMessage(error, 'Kişi güncellenemedi'));
      }
    } finally {
      setUpdatingContact(false);
    }
  };

  const handleToggleContactStatus = async (contactId: string, active: boolean) => {
    try {
      setUpdatingContactStatusId(contactId);
      await MailingService.updateContactStatus(contactId, active);
      message.success(active ? 'Kişi aktife alındı' : 'Kişi pasife alındı');
      await onReload();
    } catch (error) {
      console.error('Contact status update failed', error);
      message.error(getErrorMessage(error, 'Kişi durumu güncellenemedi'));
    } finally {
      setUpdatingContactStatusId(null);
    }
  };

  const total = filteredContacts.length;

  const columns = [
    { title: 'Ad', dataIndex: 'firstName', key: 'firstName', render: (value?: string | null) => value || '-' },
    { title: 'Soyad', dataIndex: 'lastName', key: 'lastName', render: (value?: string | null) => value || '-' },
    { title: 'E-posta', dataIndex: 'email', key: 'email' },
    { title: 'Telefon', dataIndex: 'phone', key: 'phone', render: (value?: string | null) => value || '-' },
    { title: 'Firma', dataIndex: 'companyName', key: 'companyName', render: (value?: string | null) => value || '-' },
    {
      title: 'Etiketler',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags?: string[] | null) =>
        Array.isArray(tags) && tags.length > 0 ? (
          <Space size={4} wrap>
            {tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: 'Kaynak',
      dataIndex: 'source',
      key: 'source',
      render: (_: string | null | undefined, record: MailingContact) => formatContactSourceTag(record),
    },
    {
      title: 'Durum',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean | undefined, record: MailingContact) => (
        <Switch
          checked={active !== false}
          checkedChildren='AKTIF'
          unCheckedChildren='PASIF'
          loading={updatingContactStatusId === record.id}
          onChange={(checked) => handleToggleContactStatus(record.id, checked)}
        />
      ),
    },
    {
      title: 'Oluşturma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value?: string | null) => formatDateTime(value),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: MailingContact) => (
        <Space size={8}>
          <Tooltip title='Düzenle'>
            <Button
              icon={<img src={PenIcon} alt='pen' className='icon' />}
              shape='circle'
              onClick={() => openEditDrawer(record)}
              className='icon-button'
              disabled={!record.id}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        className='br-30px kontrolx-card'
        title={
          <Row align='middle'>
            <Text style={{ fontWeight: 600 }}>Kişi Listesi</Text>
            <Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
              <strong>({total})</strong> kayıt listelendi
            </Text>
          </Row>
        }
        extra={
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-form d-input-filter'
              style={{ width: 220 }}
              value={searchInput}
              onChange={(event) => {
                const value = event.target.value || '';
                setSearchInput(value);
                if (value.length >= 2) {
                  debouncedApplySearch(value);
                } else if (value.length === 0) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Button type='primary' onClick={openFilterDrawer}>
              Filtrele
            </Button>
            <Button type='primary' onClick={openCreateDrawer}>
              Ekle
            </Button>
          </Space>
        }
      >
        <Table<MailingContact>
          rowKey='id'
          loading={loading}
          dataSource={filteredContacts}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize || DEFAULT_PAGE_SIZE);
            },
          }}
        />
      </Card>

      <Drawer open={filterOpen} onClose={closeFilterDrawer} destroyOnClose width={420} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Durum</Typography.Text>
            <Select<ContactStatusFilter>
              style={{ width: '100%', marginTop: 8 }}
              value={pendingStatusFilter}
              onChange={setPendingStatusFilter}
              options={[
                { label: 'Aktif', value: 'ACTIVE' },
                { label: 'Pasif', value: 'PASSIVE' },
              ]}
            />
          </div>

          <div>
            <Typography.Text>Kaynak</Typography.Text>
            <Select<ContactSourceFilter>
              style={{ width: '100%', marginTop: 8 }}
              value={pendingSourceFilter}
              onChange={setPendingSourceFilter}
              options={[
                { label: 'Tümü', value: 'ALL' },
                { label: 'Manuel', value: 'MANUAL' },
                { label: 'İçe Aktarım', value: 'IMPORT' },
              ]}
            />
          </div>

          <div>
            <Typography.Text>Etiketler</Typography.Text>
            <Select
              mode='multiple'
              allowClear
              style={{ width: '100%', marginTop: 8 }}
              value={pendingTagFilters}
              onChange={setPendingTagFilters}
              placeholder='Etiket seçiniz'
              options={tagOptions.map((tag) => ({ label: tag, value: tag }))}
            />
          </div>

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={clearPendingFilters}>Temizle</Button>
            <Button type='primary' onClick={applyPendingFilters}>
              Filtrele
            </Button>
          </Space>
        </Space>
      </Drawer>

      <Drawer
        open={createDrawerOpen}
        onClose={closeCreateDrawer}
        destroyOnClose
        width={560}
        title='Kişi Ekle'
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={closeCreateDrawer}>Vazgeç</Button>
            <Button type='primary' onClick={handleCreateDrawerSubmit} loading={createTab === 'manual' ? creatingContact : importingContacts}>
              {createTab === 'manual' ? 'Kaydet' : 'İçe Aktar'}
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={createTab}
          onChange={(key) => setCreateTab(key as ContactCreateTab)}
          items={[
            {
              key: 'manual',
              label: 'Manuel Ekle',
              children: (
                <Form layout='vertical' form={contactForm}>
                  <Form.Item name='firstName' label='Ad'>
                    <Input />
                  </Form.Item>
                  <Form.Item name='lastName' label='Soyad'>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name='email'
                    label='E-posta'
                    rules={[
                      { required: true, message: 'E-posta zorunludur' },
                      { type: 'email', message: 'Geçerli bir e-posta giriniz' },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item name='phone' label='Telefon'>
                    <Input />
                  </Form.Item>
                  <Form.Item name='companyName' label='Firma'>
                    <Input />
                  </Form.Item>
                  <Form.Item name='tags' label='Etiketler'>
                    <Select mode='tags' tokenSeparators={[',']} placeholder='Etiket giriniz' />
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: 'import',
              label: 'Excel Import',
              children: (
                <Space direction='vertical' size={12} style={{ width: '100%' }}>
                  <Text type='secondary'>
                    Yalnızca `.xlsx` dosyası desteklenir. Kolonlar: email (zorunlu), firstName, lastName, phone, companyName, tags.
                  </Text>
                  <Text type='secondary'>`tags` kolonunda birden fazla etiket için virgül kullanın. Örn: `vip,b2b,ankara`</Text>
                  <a href='/taslak.xlsx' download>
                    taslak.xlsx indir
                  </a>
                  <Upload
                    accept='.xlsx'
                    maxCount={1}
                    fileList={importFileList}
                    beforeUpload={() => false}
                    onChange={({ fileList }) => setImportFileList(fileList)}
                    onRemove={() => {
                      setImportFileList([]);
                      setImportResult(null);
                    }}
                  >
                    <Button>Dosya Seç</Button>
                  </Upload>

                  {importResult && (
                    <Alert
                      type='info'
                      showIcon
                      message={`Toplam Satır: ${importResult.totalRows} | Eklenen: ${importResult.createdCount} | Mükerrer: ${importResult.skippedDuplicateCount} | Geçersiz: ${importResult.skippedInvalidCount}`}
                      description={
                        importResult.errors?.length ? (
                          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                            {importResult.errors.map((error, index) => (
                              <div key={`${error.rowNumber}-${index}`}>
                                Satır {error.rowNumber} ({error.email || '-'}) - {error.message}
                              </div>
                            ))}
                          </div>
                        ) : undefined
                      }
                    />
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Drawer>

      <Drawer
        open={editDrawerOpen}
        onClose={closeEditDrawer}
        destroyOnClose
        width={520}
        title='Kişi Düzenle'
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={closeEditDrawer}>Vazgeç</Button>
            <Button type='primary' onClick={handleUpdateContact} loading={updatingContact}>
              Güncelle
            </Button>
          </Space>
        }
      >
        <Form layout='vertical' form={contactEditForm}>
          <Form.Item name='firstName' label='Ad'>
            <Input />
          </Form.Item>
          <Form.Item name='lastName' label='Soyad'>
            <Input />
          </Form.Item>
          <Form.Item
            name='email'
            label='E-posta'
            rules={[
              { required: true, message: 'E-posta zorunludur' },
              { type: 'email', message: 'Geçerli bir e-posta giriniz' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name='phone' label='Telefon'>
            <Input />
          </Form.Item>
          <Form.Item name='companyName' label='Firma'>
            <Input />
          </Form.Item>
          <Form.Item name='tags' label='Etiketler'>
            <Select mode='tags' tokenSeparators={[',']} placeholder='Etiket giriniz' />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default MailingContactsTab;
